/** Apply a ui_patch to the assistant message that owns the target part. */
export function applyUiPatchToMessages(messages, patch) {
  if (!patch?.part_id) return messages;
  const next = [...messages];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    if (next[i].role !== 'assistant') continue;
    const parts = [...(next[i].ui_parts || [])];
    const pi = parts.findIndex((p) => p.part_id === patch.part_id);
    if (pi < 0) continue;
    const merge = patch.merge !== false;
    parts[pi] = merge
      ? { ...parts[pi], props: { ...parts[pi].props, ...patch.props } }
      : { ...parts[pi], props: patch.props };
    next[i] = { ...next[i], ui_parts: parts };
    return next;
  }
  return messages;
}

/** Merge session ui_parts props into whichever assistant messages already own them. */
export function mergeSessionUiPartsIntoMessages(messages, sessionParts) {
  if (!sessionParts?.length) return messages;
  const byId = new Map(sessionParts.map((p) => [p.part_id, p]));
  let changed = false;
  const next = messages.map((msg) => {
    if (msg.role !== 'assistant' || !msg.ui_parts?.length) return msg;
    let msgChanged = false;
    const parts = msg.ui_parts.map((part) => {
      const updated = byId.get(part.part_id);
      if (!updated) return part;
      msgChanged = true;
      return {
        ...part,
        props: { ...part.props, ...updated.props },
        actions: updated.actions || part.actions,
      };
    });
    if (!msgChanged) return msg;
    changed = true;
    return { ...msg, ui_parts: parts };
  });
  return changed ? next : messages;
}

/** Build a user-visible chat message for a UI turn action. */
export function formatUiActionLabel(actionId, payload = {}) {
  if (payload.label) return payload.label;

  const item = payload.item || {};

  if (actionId === 'seat.done') {
    const labels = payload.seat_labels || [];
    if (labels.length) {
      return `Continue with seat${labels.length > 1 ? 's' : ''} ${labels.join(', ')}`;
    }
    const seats = payload.seat_ids || [];
    return seats.length
      ? `Continue with ${seats.length} seat${seats.length > 1 ? 's' : ''}`
      : 'Continue';
  }

  if (actionId === 'seat.skip') {
    return 'Auto-assign seats';
  }

  if (actionId === 'booking.pay') {
    return payload.label || 'Pay & get tickets';
  }

  if (actionId?.startsWith('seat.toggle')) {
    return payload.seat_label
      ? `Seat ${payload.seat_label}`
      : payload.seat_id != null
        ? `Seat ${payload.seat_id}`
        : 'Seat selection';
  }

  if (payload.scheduled_trip_id != null || item.plannedStartTime || item.startTimeUtc) {
    const time = payload.time || item.plannedStartTime || item.startTimeUtc || '';
    const short = String(time).slice(0, 5);
    const fare = item.costAmount ?? item.fare;
    if (short && fare != null) return `Trip at ${short} (${fare} AED)`;
    if (short) return `Trip at ${short}`;
    return `Trip #${payload.scheduled_trip_id ?? payload.value}`;
  }

  if (item.title) return item.title;
  if (item.name) return item.name;
  if (item.label) return item.label;

  if (payload.route_pattern_id != null) {
    return item.title || item.name || `Route #${payload.route_pattern_id}`;
  }

  if (payload.value != null) return String(payload.value);

  const fallbacks = {
    'choice.select': 'Selected option',
    'form.submit': 'Submitted',
    'write.confirm': 'Allow',
    'write.always_allow': 'Always allow',
    'write.deny': 'Deny',
  };
  return fallbacks[actionId] || actionId;
}

const UI_SUPERSEDES = {
  choice_list: new Set(),
  trip_options: new Set(['choice_list', 'route_timeline', 'vertical:transport.route_timeline.v1']),
  'vertical:transport.trip_options.v1': new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.route_timeline.v1',
  ]),
  route_timeline: new Set(['choice_list']),
  'vertical:transport.route_timeline.v1': new Set(['choice_list', 'route_timeline']),
  seat_map: new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
  ]),
  'vertical:transport.seat_map.v1': new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
  ]),
  hold_timer: new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
    'seat_map',
    'vertical:transport.seat_map.v1',
  ]),
  'vertical:transport.hold_timer.v1': new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
    'seat_map',
    'vertical:transport.seat_map.v1',
  ]),
  'vertical:transport.ticket_qr.v1': new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
    'seat_map',
    'vertical:transport.seat_map.v1',
    'hold_timer',
    'vertical:transport.hold_timer.v1',
    'checkout',
    'vertical:transport.checkout.v1',
  ]),
  checkout: new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
    'seat_map',
    'vertical:transport.seat_map.v1',
    'hold_timer',
    'vertical:transport.hold_timer.v1',
  ]),
  'vertical:transport.checkout.v1': new Set([
    'choice_list',
    'trip_options',
    'route_timeline',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
    'seat_map',
    'vertical:transport.seat_map.v1',
    'hold_timer',
    'vertical:transport.hold_timer.v1',
  ]),
};

const COMPONENT_EQUIV = {
  trip_options: 'vertical:transport.trip_options.v1',
  seat_map: 'vertical:transport.seat_map.v1',
  hold_timer: 'vertical:transport.hold_timer.v1',
  checkout: 'vertical:transport.checkout.v1',
  ticket_qr: 'vertical:transport.ticket_qr.v1',
  route_timeline: 'vertical:transport.route_timeline.v1',
};

function componentFamily(component) {
  const family = new Set([component]);
  Object.entries(COMPONENT_EQUIV).forEach(([primitive, vertical]) => {
    if (component === primitive || component === vertical) {
      family.add(primitive);
      family.add(vertical);
    }
  });
  return family;
}

function uiPartHasContent(part) {
  if (!part) return false;
  const props = part.props || {};
  const listKeys = ['trips', 'items', 'stops', 'seats', 'tickets', 'rows', 'conflicts'];
  for (const key of listKeys) {
    const val = props[key];
    if (Array.isArray(val) && val.length > 0) return true;
  }
  const component = part.component || '';
  if (component.includes('hold_timer') || component.includes('checkout') || component === 'user_gate_bar') return true;
  if (component.includes('ticket_qr') && (props.tickets?.length > 0 || props.invoiceId)) return true;
  if (component.includes('seat_map') && props.seats?.length > 0) return true;
  if (component === 'retake_timeline' && (props.items?.length > 0 || props.retakes?.length > 0)) return true;
  if (component === 'retake_conflicts' && props.conflicts?.length > 0) return true;
  if (component === 'retake_export_bar' && props.group) return true;
  if (component === 'data_table' && props.rows?.length > 0) return true;
  if (component === 'campus_map' && props.src === 'https://ischemes.ru/group/rtu-mirea/vern78') return true;
  return false;
}

/** Latest single widget for post-selection booking stages (ticket > hold > seats). */
const BOOKING_FLOW_COMPONENTS = [
  'vertical:transport.ticket_qr.v1',
  'ticket_qr',
  'vertical:transport.checkout.v1',
  'checkout',
  'vertical:transport.hold_timer.v1',
  'hold_timer',
  'vertical:transport.seat_map.v1',
  'seat_map',
];

function latestBookingFlowPart(parts) {
  for (const key of BOOKING_FLOW_COMPONENTS) {
    const found = parts.find((p) => (p.component || '') === key);
    if (found && uiPartHasContent(found)) return found;
  }
  return null;
}

/** Primitive widgets that add noise during transport booking — vertical widgets are enough. */
const BUBBLE_SUPPRESSED_COMPONENTS = new Set([
  'choice_list',
  'summary_card',
  'route_timeline',
  'vertical:transport.route_timeline.v1',
]);

function stripHoldWhenTicketPresent(parts) {
  const hasTicket = (parts || []).some((p) => (p.component || '').includes('ticket_qr'));
  if (!hasTicket) return parts || [];
  return (parts || []).filter((p) => !(p.component || '').includes('hold_timer'));
}

/** Parts rendered inside the assistant bubble (excludes trace-column widgets). */
export function bubbleUiParts(parts) {
  const drop = new Set(['user_gate_bar']);
  const cleaned = stripHoldWhenTicketPresent(parts);
  return filterDisplayableUiParts(
    cleaned.filter((p) => !drop.has(p.component || '')),
  ).filter((p) => !BUBBLE_SUPPRESSED_COMPONENTS.has(p.component || ''));
}

export function findUserGatePart(parts) {
  return (parts || []).find((p) => (p.component || '') === 'user_gate_bar') || null;
}

export function findInteractionBarPart(parts) {
  return findUserGatePart(parts);
}

/** Drop in-flight tool rows from a pipeline trace snapshot (awaiting-confirm leftovers). */
export function stripEphemeralToolStepsFromTrace(trace) {
  if (!trace?.steps?.length) return trace;
  const steps = trace.steps.filter(
    (step) => !(step?.id === 'agent_tool' && step?.status === 'running'),
  );
  if (steps.length === trace.steps.length) return trace;
  return { ...trace, steps };
}

/** Prefer done ui_parts when they are displayable; otherwise keep streamed parts. */
export function resolveDoneUiParts(doneParts, fallbackParts) {
  const done = Array.isArray(doneParts) ? doneParts : [];
  if (filterDisplayableUiParts(done).length > 0) return done;
  const fallback = Array.isArray(fallbackParts) ? fallbackParts : [];
  if (filterDisplayableUiParts(fallback).length > 0) return fallback;
  return done.length > 0 ? done : fallback;
}

/** Keep one selection widget plus ancillary widgets; drop empty parts. */
export function filterDisplayableUiParts(parts) {
  if (!parts?.length) return [];

  const bookingWidget = latestBookingFlowPart(parts);
  if (bookingWidget) {
    return [bookingWidget];
  }

  const selectionComponents = new Set([
    'choice_list',
    'trip_options',
    'vertical:transport.trip_options.v1',
    'vertical:transport.route_timeline.v1',
    'route_timeline',
  ]);
  let latestSelection = null;
  const ancillary = new Map();

  parts.forEach((part) => {
    if (!uiPartHasContent(part)) return;
    const component = part.component || '';
    if (selectionComponents.has(component)) {
      latestSelection = part;
    } else {
      ancillary.set(component, part);
    }
  });

  const out = [];
  if (latestSelection) out.push(latestSelection);
  ancillary.forEach((part) => out.push(part));
  return compactMireaStudentParts(out);
}

/** Professor student list supersedes a redundant retake timeline in the same turn. */
function compactMireaStudentParts(parts) {
  const hasStudentTable = (parts || []).some(
    (p) => p.component === 'data_table' && (p.props?.exportKind === 'students' || p.props?.export_kind === 'students'),
  );
  if (!hasStudentTable) return parts || [];
  return (parts || []).filter((p) => p.component !== 'retake_timeline');
}

/** Keep only the latest relevant widget(s) in a booking flow. */
export function upsertUiPart(parts, newPart) {
  if (!uiPartHasContent(newPart)) return parts || [];
  const component = newPart?.component || '';
  const drop = new Set();
  componentFamily(component).forEach((key) => {
    const superseded = UI_SUPERSEDES[key];
    if (superseded) superseded.forEach((c) => drop.add(c));
    componentFamily(key).forEach((c) => drop.add(c));
  });
  const kept = (parts || []).filter((p) => {
    const fam = componentFamily(p.component || '');
    for (const c of fam) {
      if (drop.has(c)) return false;
    }
    return true;
  });
  return [...kept, newPart];
}
